import type { PurchaseLinePreview } from '../types/inventory';

/**
 * Fase 22C-5 — Lógica de presentación (pura) de la vista previa de normalización de una línea de compra.
 * NO recalcula nada financiero: solo interpreta el resultado autoritativo que devolvió el backend para
 * decidir qué mostrar y si se debe bloquear el botón "Agregar". La aritmética (equivalencia a unidad
 * base, costo normalizado, impacto en promedio) SIEMPRE la hace el backend.
 */
export interface PurchaseLinePreviewView {
  blocksAdd: boolean;
  errorMessage: string | null;
  /** Frase explícita de la consecuencia (solo cuando la normalización fue OK). */
  consequence: string | null;
  /** Impacto en el costo promedio, ya formateado, o null si no aplica / sin permiso de costos. */
  avgImpact: string | null;
}

export function describePurchaseLinePreview(preview: PurchaseLinePreview | undefined, showCosts: boolean): PurchaseLinePreviewView {
  if (!preview) return { blocksAdd: false, errorMessage: null, consequence: null, avgImpact: null };

  if (preview.status === 'INCOMPATIBLE_UNIT') {
    return {
      blocksAdd: true,
      errorMessage: `La unidad «${preview.enteredUnitSymbol}» no es convertible a la unidad base del insumo (${preview.baseUnitSymbol}). Elige una unidad compatible antes de agregar la línea.`,
      consequence: null,
      avgImpact: null,
    };
  }
  if (preview.status === 'INVALID_QUANTITY') {
    return { blocksAdd: true, errorMessage: 'La cantidad debe ser mayor que cero.', consequence: null, avgImpact: null };
  }
  if (preview.status === 'INVALID_COST') {
    return { blocksAdd: true, errorMessage: 'El costo no puede ser negativo.', consequence: null, avgImpact: null };
  }

  // status === 'OK'
  const consequence = preview.baseQuantity && preview.normalizedUnitCost
    ? `Se agregarán ${preview.baseQuantity} ${preview.baseUnitSymbol} a $${preview.normalizedUnitCost} por ${preview.baseUnitSymbol}. El costo promedio final lo recalcula el sistema al confirmar la compra.`
    : null;

  const avgImpact = showCosts && preview.estimatedNewAvgCost
    ? `${preview.currentAvgCost ? `$${preview.currentAvgCost} → ` : ''}$${preview.estimatedNewAvgCost} / ${preview.baseUnitSymbol}`
    : null;

  return { blocksAdd: false, errorMessage: null, consequence, avgImpact };
}
